import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { Communication, ExternalDialogEvent, Thread } from '../../interface';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { SkyI18nModule, registerTranslationsManuallyStatic } from '@skyeng/libs/shared/i18n';
import { MessageType } from '../../const';
import { Store } from '@skyeng/libs/shared/store';
import { CommunicationFormService } from '../communication-form/communication-form.service';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { DecorateUntilDestroy, filterEmpty, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { VimCoreCurrentUserService } from '@skyeng/libs/vimbox-core/user';
import { BehaviorSubject, combineLatest, fromEvent, Observable, timer } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { DatePipe } from '@angular/common';
import { CommunicationsFormComponent } from '../communication-form/communication-form';
import { CommunicationFlatViewMessageComponent } from '../communication-flat-view-message/communication-flat-view-message';
import { DsButtonModule } from '@skyeng/libs/shared/ui-kit-v2/button';
import { DsIconModule } from '@skyeng/libs/shared/ui-kit-v2/icon';
import { FeatureFlagsService } from '@skyeng/libs/skypro/shared';
import empty1 from './images/empty.svg';
import entranslation from '../../translation/en.json';
import rutranslation from '../../translation/ru.json';

// Значение подобрано экспериментально, чтобы при скроле не было остановок на подгрузку на хорошем интернете
const DISTANCE_TO_TOP_WHEN_SHOULD_FETCH = 3000;
// По смыслу это 3-5 сообщений
const DISTANCE_WHEN_SHOW_SCROLL_TO_LATEST = 300;

interface CombinedMessage {
  id: number;
  type: 'thread' | 'external';
  content: Thread | ExternalDialogEvent;
  createdAt: string;
}

/**
 * Разница между этим компонентом и skypro-communication в том, что:
 * - skypro-communication-flat-view: хронологический (новые снищу), нет вложенности
 * - skypro-communication: обратный хронологический (новые сверху), есть вложенность (можно ответить внутри треда) + всякая мелочевка (лайки, пины etc)
 */
@DecorateUntilDestroy()
@Component({
  selector: 'skypro-communication-flat-view',
  templateUrl: 'communication-flat-view.html',
  styleUrl: 'communication-flat-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SkyI18nModule,
    CommunicationFlatViewMessageComponent,
    CommunicationsFormComponent,
    DsButtonModule,
    DsIconModule,
    AsyncPipe,
    DatePipe,
    TranslocoModule,
  ],
})
export class CommunicationFlatViewComponent implements OnInit, OnChanges {
  private featureFlagsService = inject(FeatureFlagsService);
  private translocoService = inject(TranslocoService);
  private communicationFormService = inject(CommunicationFormService);
  private vimCoreCurrentUserService = inject(VimCoreCurrentUserService);

  MessageType = MessageType;

  @Input() communicationId: number | null;
  @Input() needSaveInProgressText = false;
  @Input() localStorageKey: string | null = null;
  @Input() externalDialogEvents: ExternalDialogEvent[] = [];

  readonly inputDisabledPlaceholder = input<string | null>(null);
  baseApiHost = input('');

  @Output() threadCreated = new EventEmitter<void>();
  @Output() createAiMessageReport = new EventEmitter<{ threadId: number; threadText: string }>();
  @Output() lastThreadReached = new EventEmitter<void>();
  @Output() communicationChanged = new EventEmitter<void>();
  @Output() newThreadsReceived = new EventEmitter<{ isAtBottom: boolean }>();

  @ViewChildren('threadRefs', { read: ElementRef }) threadRefs!: QueryList<ElementRef>;
  @ViewChild('threadsContainer') threadsContainer: ElementRef<HTMLElement>;

  empty = empty1;
  // Описан тут: packages/skypro/student-care/src/app/feature-flags.ts
  isDebugCuratorTalksEnabled = this.featureFlagsService.isFeatureFlagEnabled('debugCuratorTalks');

  private store = new Store({
    communication: null as Communication | null,
    communicationId: null as number | null,
    // Флаг загрузки коммуникации внутри шорт поллинга
    isLoading: false,
    // id самого свежего сообщения, которое видел юзер
    latestSeenByUserThreadId: null as number | null,
    editingMessageId: null as number | null,
    threadsScrollDistance: {
      // +1 чтобы не срабатывала загрузка при открытии страницы
      distanceToTop: DISTANCE_TO_TOP_WHEN_SHOULD_FETCH + 1,
      // -1 чтобы при открытии страницы не отображалась кнопка + чтобы проскролить в самый конец
      distanceToBottom: DISTANCE_WHEN_SHOW_SCROLL_TO_LATEST - 1,
    },
  });

  // uglyhack: Я не знаю почему, но если сделать тоже самое через store, флаг не выставляется в true при смене коммуникации.
  private isInitialCommunicationLoadingSubj = new BehaviorSubject<boolean>(true);
  // Флаг первичной загрузки коммуникации, выставляется в true при смене коммуникации, не меняется при шорт поллинге
  isInitialCommunicationLoading$ = this.isInitialCommunicationLoadingSubj.asObservable();

  // id треда до которого до скролил юзер
  private earliestThreadSeenByUser$: Observable<number | null>;
  private latestSeenByUserThreadId$ = this.store.select('latestSeenByUserThreadId');

  editingMessageId$ = this.store.select('editingMessageId');

  communication$ = this.store.select('communication').pipe(filterEmpty());
  threadCount$ = this.communication$.pipe(map(({ threadsCount }) => threadsCount));
  threads$ = this.communication$.pipe(map(({ threads }) => [...threads].reverse()));
  currentUser$ = this.vimCoreCurrentUserService.authorizedUser$.pipe(filterEmpty());
  currentUserId = this.vimCoreCurrentUserService.getUser()?.id;
  isLoading$ = this.communicationFormService.isLoading$.pipe(startWith(true));

  private threadsScrollDistance$ = this.store.select('threadsScrollDistance');

  showScrollToLatestButton$ = this.threadsScrollDistance$.pipe(map(() => this.showScrollToLatestButtonV2()));

  newMessagesCount$ = combineLatest([this.communication$, this.latestSeenByUserThreadId$]).pipe(
    map(([communication, latestThreadId]) => {
      if (!latestThreadId) {
        return 0;
      }

      const latestThreadIndex = this.getThreadIndex(latestThreadId, communication);
      return latestThreadIndex === -1 ? 0 : latestThreadIndex;
    }),
  );

  combinedMessages$ = this.threads$.pipe(
    map(threads => {
      let events = this.externalDialogEvents || [];
      events = [...events, ...this.getEventsFromThreads(threads)];

      const combined: CombinedMessage[] = [
        ...threads.map(
          (thread: Thread): CombinedMessage => ({
            id: thread.id,
            type: 'thread',
            content: thread,
            createdAt: thread.createdAt,
          }),
        ),
        ...events.map(
          (event: ExternalDialogEvent): CombinedMessage => ({
            id: event.id,
            type: 'external',
            content: event,
            createdAt: event.createdAt,
          }),
        ),
      ];

      // Сортируем по дате создания (новые внизу)
      return [...combined].sort((first, second) => {
        const firstDate = new Date(first.createdAt).getTime();
        const secondDate = new Date(second.createdAt).getTime();
        return firstDate - secondDate;
      });
    }),
  );

  constructor() {
    registerTranslationsManuallyStatic('skyproCommunications', this.translocoService, {
      en: entranslation,
      ru: rutranslation,
    });
  }

  private getEventsFromThreads(threads: Thread[]): ExternalDialogEvent[] {
    return threads
      .filter((thread: Thread) => {
        const meta = thread.meta as { amoTaskResolution?: string };
        return meta?.amoTaskResolution;
      })
      .map((thread: Thread) => {
        const meta = thread.meta as { amoTaskResolution?: string };
        return {
          id: thread.id,
          text: `Закрыта задача в AMO: ${meta.amoTaskResolution}`,
          type: 'closed',
          createdAt: thread.createdAt,
          meta: {},
        };
      });
  }

  private showScrollToLatestButtonV2(): boolean {
    const { distanceToBottom } = this.store.get('threadsScrollDistance');

    return distanceToBottom > DISTANCE_WHEN_SHOW_SCROLL_TO_LATEST;
  }

  isThread(message: CombinedMessage): message is CombinedMessage & { type: 'thread'; content: Thread } {
    return message.type === 'thread';
  }

  isExternalEvent(
    message: CombinedMessage,
  ): message is CombinedMessage & { type: 'external'; content: ExternalDialogEvent } {
    return message.type === 'external';
  }

  isToday(dateIso: string): boolean {
    const date = new Date(dateIso);
    const now = new Date();
    return (
      date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.communicationId) {
      this.setCommunicationId(this.communicationId);
    }
  }

  ngOnInit(): void {
    this.initEarliestThreadSeenByUser();
    this.initCommunicationPolling();

    // Записываем положение скрола в стор
    this.initScrollListeners();
  }

  identify(_index: number, item: { id: number }): number {
    return item.id;
  }

  setCommunicationId(communicationId: number | null): void {
    this.store.updateField('communicationId', communicationId);
  }

  onThreadCreated(): void {
    this.scrollToLatestThread();
    this.threadCreated.emit();
  }

  scrollToLatestThread(behavior: ScrollBehavior = 'smooth'): void {
    const threadEls: ElementRef<HTMLElement>[] = this.threadRefs.toArray();

    if (!threadEls.length) {
      return;
    }

    this.scrollToThread(threadEls.length - 1, behavior);
  }

  deleteThread(threadId: number): void {
    this.communicationFormService.deleteThread(threadId).pipe(takeUntilDestroyed(this)).subscribe();
  }

  doCreateAiMessageReport(thread: Thread): void {
    this.createAiMessageReport.emit({ threadId: thread.id, threadText: thread.body });
  }

  startEditing(messageId: number): void {
    this.store.updateField('editingMessageId', messageId);
  }

  finishEditing(): void {
    this.store.updateField('editingMessageId', null);
  }

  private initScrollListeners(): void {
    // Записываем положение скрола в стор
    fromEvent(window, 'scroll', { capture: true })
      .pipe(debounceTime(300), takeUntilDestroyed(this))
      .subscribe(() => {
        const pixelsToTop = this.getPixelsToTop();
        const pixelsToBottom = this.getPixelsToBottom();
        this.store.updateFields({
          threadsScrollDistance: { distanceToTop: pixelsToTop, distanceToBottom: pixelsToBottom },
        });
      });

    // отмечаем последнее сообщение прочитанным если юзер не отскролил наверх: при получении новых сообщений и при изменении скрола
    combineLatest([this.communication$, this.showScrollToLatestButton$])
      .pipe(takeUntilDestroyed(this))
      .subscribe(([communication, showScrollToLatestButton]) => {
        if (showScrollToLatestButton) {
          return;
        }

        this.store.updateFields({ latestSeenByUserThreadId: this.getLatestThreadIdFromCommunication(communication) });
      });

    // эмитим событие когда пользователь доскролил до последнего сообщения
    combineLatest([this.communication$, this.threadsScrollDistance$])
      .pipe(
        map(([communication, scrollDistance]) => {
          if (!communication) {
            return false;
          }
          return scrollDistance.distanceToBottom <= DISTANCE_WHEN_SHOW_SCROLL_TO_LATEST;
        }),
        distinctUntilChanged(),
        filter(isAtBottom => isAtBottom),
        takeUntilDestroyed(this),
      )
      .subscribe(() => {
        this.lastThreadReached.emit();
      });

    // автоматический скролл при появлении новых сообщений
    this.threads$
      .pipe(
        map(threads => {
          if (threads.length === 0) {
            return null;
          }

          const latestThreadId = threads[threads.length - 1].id;

          return latestThreadId;
        }),
        filterEmpty(),
        // Обновился последний тред в переписке, неважно кто автор: текущий юзер или удаленный собеседник
        distinctUntilChanged(),
        delay(0), // нужен чтобы интерфейс успел перерисоваться, и после этого сработал скрол
        takeUntilDestroyed(this),
      )
      .subscribe(() => {
        // Добавился новый тред, решаем будем ли скролить
        const showScrollToLatestButton = this.showScrollToLatestButtonV2();
        const shouldScroll = !showScrollToLatestButton;

        if (shouldScroll) {
          this.scrollToLatestThread();
        }
      });

    // эмитим событие при получении новых сообщений
    combineLatest([this.threads$, this.threadsScrollDistance$])
      .pipe(
        map(([threads, scrollDistance]) => {
          if (threads.length === 0) {
            return null;
          }

          const latestThreadId = threads[threads.length - 1].id;
          const isAtBottom = scrollDistance.distanceToBottom <= DISTANCE_WHEN_SHOW_SCROLL_TO_LATEST;

          return { latestThreadId, isAtBottom };
        }),
        filterEmpty(),
        distinctUntilChanged((prev, curr) => prev.latestThreadId === curr.latestThreadId),
        takeUntilDestroyed(this),
      )
      .subscribe(({ isAtBottom }) => {
        this.newThreadsReceived.emit({ isAtBottom });
      });
  }

  private initCommunicationPolling(): void {
    combineLatest([
      this.store.select('communicationId').pipe(
        tap(() => {
          this.isInitialCommunicationLoadingSubj.next(true);
        }),
        filterEmpty(),
      ),
      this.earliestThreadSeenByUser$,
    ])
      .pipe(
        switchMap(([communicationId, earliestThreadId]) => {
          return this.communicationFormService.pollCommunication(communicationId, earliestThreadId, false);
        }),
        map(result => this.filterCommunication(result)),
        tap(() => {
          this.isInitialCommunicationLoadingSubj.next(false);
        }),
        takeUntilDestroyed(this),
      )
      .subscribe(communication => {
        this.store.updateFields({
          communication,
        });
      });

    // эмитим событие при смене communicationId
    this.store
      .select('communicationId')
      .pipe(filterEmpty(), distinctUntilChanged(), takeUntilDestroyed(this))
      .subscribe(() => {
        this.communicationChanged.emit();
      });
  }

  private scrollToThread(index = 0, behavior: ScrollBehavior = 'smooth'): void {
    // timer нужен чтобы успел отрисоваться новый тред, а потом сработал скролл
    // Криво, зато просто
    timer(0)
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => {
        const threadEls: ElementRef<HTMLElement>[] = this.threadRefs.toArray();
        if (!threadEls.length) {
          return;
        }

        threadEls[index].nativeElement.scrollIntoView({ behavior });
      });
  }

  private initEarliestThreadSeenByUser(): void {
    this.earliestThreadSeenByUser$ = this.store.select('communicationId').pipe(
      // Когда меняется коммуникация, нам нужно снова начать с null, поэтому используем вложенный поток
      switchMap(() =>
        this.threadsScrollDistance$.pipe(
          skip(1),
          filter(({ distanceToTop }) => {
            const isAllThreadsAlreadyLoaded =
              this.store.get('communication')?.threadsCount === this.store.get('communication')?.threads.length;

            if (isAllThreadsAlreadyLoaded) {
              return false;
            }

            // Когда до нижней границы остается немного - запрашиваем новый чанк
            return distanceToTop < DISTANCE_TO_TOP_WHEN_SHOULD_FETCH;
          }),
          map(() => {
            const communication = this.store.get('communication');
            if (!communication) {
              return null;
            }

            return this.getEarliestThreadIdFromCommunication(communication);
          }),
          distinctUntilChanged(),
          startWith(null),
        ),
      ),
    );
  }

  private getPixelsToBottom(): number {
    if (!this.threadsContainer) {
      return 0; // или -1, чтобы указать, что контейнер не установлен
    }
    const containerHeight = this.threadsContainer.nativeElement.scrollHeight;
    const scrollTop = this.threadsContainer.nativeElement.scrollTop;
    const containerVisibleHeight = this.threadsContainer.nativeElement.clientHeight;

    const pixelsToBottom = containerHeight - (scrollTop + containerVisibleHeight);
    return pixelsToBottom;
  }

  private getPixelsToTop(): number {
    if (!this.threadsContainer) {
      return 0; // или -1, чтобы указать, что контейнер не установлен
    }
    const scrollTop = this.threadsContainer.nativeElement.scrollTop;

    return scrollTop;
  }

  private getEarliestThreadIdFromCommunication(communication: Communication): number | null {
    return communication.threads.length === 0 ? null : communication.threads[communication.threads.length - 1].id;
  }

  private getLatestThreadIdFromCommunication(communication: Communication): number | null {
    return communication.threads.length === 0 ? null : communication.threads[0].id;
  }

  private getThreadIndex(threadId: number, communication: Communication): number {
    return communication.threads.findIndex(({ id }) => id === threadId);
  }

  private filterDeletedThreads(communication: Communication): Communication {
    return {
      ...communication,
      threads: communication.threads.filter(({ isDeleted, messages }) => {
        if (!isDeleted) {
          return true;
        }

        return messages.length > 0;
      }),
    };
  }

  private filterCommunication(communication: Communication): Communication {
    let processedCommunication = this.getCommunicationWithTextMessages(communication);

    processedCommunication = this.filterDeletedThreads(processedCommunication);

    return processedCommunication;
  }

  private getCommunicationWithTextMessages(communication: Communication): Communication {
    return {
      ...communication,
      threads: communication.threads.map(thread => {
        return {
          ...thread,
          messages: thread.messages.filter(({ type }) => type === MessageType.text),
        };
      }),
    };
  }
}
